import React from 'react';
import { Icon } from '@iconify/react';

interface HandoffLoaderProps {
  icon: string;
  label: string;
}

/**
 * Full-screen aesthetic loader shown while a handoff token is being minted
 * and the browser is redirecting to the selected module.
 * Blocks interaction to prevent double-taps during the redirect.
 */
const HandoffLoader: React.FC<HandoffLoaderProps> = ({ icon, label }) => {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-xl animate-kiosk-fade-in"
      role="status"
      aria-live="polite"
      aria-label={`Opening ${label}`}
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/15 via-transparent to-primary/5" />

      <div className="relative flex flex-col items-center gap-10 animate-kiosk-scale-in">
        <div className="relative flex h-48 w-48 items-center justify-center">
          <span className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
          <span
            className="absolute inset-4 rounded-full bg-primary/25 animate-ping"
            style={{ animationDelay: '200ms' }}
          />
          <span className="absolute inset-8 rounded-full bg-primary/30 animate-pulse" />
          <div className="relative flex h-28 w-28 items-center justify-center rounded-3xl bg-primary text-primary-foreground shadow-2xl shadow-primary/40">
            <Icon icon={icon} width={64} height={64} aria-hidden="true" />
          </div>
        </div>

        <div className="text-center">
          <h2 className="text-3xl font-semibold tracking-tight text-foreground">
            Opening {label}
          </h2>
          <p className="mt-2 text-base text-default-500">Just a moment…</p>
        </div>

        <div className="flex gap-2" aria-hidden="true">
          <span
            className="h-3 w-3 rounded-full bg-primary animate-bounce"
            style={{ animationDelay: '0ms' }}
          />
          <span
            className="h-3 w-3 rounded-full bg-primary animate-bounce"
            style={{ animationDelay: '150ms' }}
          />
          <span
            className="h-3 w-3 rounded-full bg-primary animate-bounce"
            style={{ animationDelay: '300ms' }}
          />
        </div>
      </div>
    </div>
  );
};

export default HandoffLoader;
